import axios from "axios";
import * as Sentry from "@sentry/react";

// Only use Sentry in production environment
const isProduction = process.env.NODE_ENV === "production";
import { api } from "./configs/axiosConfigs";
import { defineCancelApiObject } from "./configs/axiosUtils";
import { UploadQueue } from "../utils/UploadQueue";

export const CsrAPI = {
  _uploadQueue: new UploadQueue(),
  getAllStudies: async function (cancel = false) {
    const response = await api.request({
      url: "/dashboard-documents",
      method: "GET",
      signal: cancel
        ? cancelApiObject[this.getAllStudies.name].handleRequestCancellation()
            .signal
        : undefined,
    });
    return response.data.documents;
  },
  createStudy: async function ({ studyName, studyType }, cancel = false) {
    const response = await api.request({
      url: "/studies",
      method: "POST",
      params: { study_name: studyName, study_type: studyType },
      signal: cancel
        ? cancelApiObject[this.createStudy.name].handleRequestCancellation()
            .signal
        : undefined,
    });
    return response.data.attribute_values;
  },
  getOneStudy: async function ({ studyId }, cancel = false) {
    const response = await api.request({
      url: "/studies/" + studyId,
      method: "GET",
      signal: cancel
        ? cancelApiObject[this.getOneStudy.name].handleRequestCancellation()
            .signal
        : undefined,
    });

    return response.data.attribute_values;
  },
  getQuestions: async function ({ studyId }, cancel = false) {
    // TEMPORARY: Comment out API call for development purposes
    const response = await api.request({
      url: "/studies/" + studyId + "/questions",
      method: "GET",
      signal: cancel
        ? cancelApiObject[this.getQuestions.name].handleRequestCancellation()
            .signal
        : undefined,
    });

    return response.data;

    // TEMPORARY: Load data from local JSON file instead
    // const questionsData = await import("../assets/json/questions.json");
    // return questionsData.default;
  },
  saveQuestions: async function ({ studyId, state }, cancel = false) {
    const response = await api.request({
      url: "/studies/" + studyId + "/questions",
      method: "PUT",
      data: state,
      signal: cancel
        ? cancelApiObject[this.saveQuestions.name].handleRequestCancellation()
            .signal
        : undefined,
    });

    return response.data;
  },
  saveDataMappingTablesSelection: async function (
    { studyId, state },
    cancel = false
  ) {
    const response = await api.request({
      url: "/studies/" + studyId + "/filter",
      method: "PUT",
      data: state,
      signal: cancel
        ? cancelApiObject[
            this.saveDataMappingTablesSelection.name
          ].handleRequestCancellation().signal
        : undefined,
    });

    return response.data;
  },
  async uploadDocument({ studyId, file, docType }, onUploadProgress) {
    if (isProduction) {
      Sentry.setContext("file", {
        name: file.name,
        size: file.size,
      });
    }

    const uploadFn = async uploadState => {
      const initResponse = await this._initUpload(studyId, file, docType);
      const { presigned_url, document_id } = initResponse.data;

      uploadState.documentId = document_id;

      await this._uploadToPresignedUrl(
        studyId,
        file,
        docType,
        presigned_url,
        onUploadProgress
      );

      return { success: true };
    };

    const deleteDocumentFn = async uploadState => {
      await this.deleteDocument({
        studyId,
        documentId: uploadState.documentId,
      });
    };

    return this._uploadQueue.add({
      file,
      uploadFn,
      deleteDocumentFn,
    });
  },

  async _initUpload(studyId, file, docType) {
    return api.request({
      url: `/studies/${studyId}/upload/init`,
      method: "POST",
      params: { document_type: docType },
      data: {
        filename: file.name,
        fileSize: file.size,
      },
    });
  },

  async _uploadToPresignedUrl(
    studyId,
    file,
    docType,
    presignedUrl,
    onUploadProgress
  ) {
    try {
      await axios.put(presignedUrl, file, {
        headers: {
          "Content-Type": undefined,
          Authorization: undefined,
        },
        onUploadProgress,
      });
    } catch (error) {
      // If 403, the presigned URL has expired — get a new one and retry once
      if (error.response?.status === 403) {
        const retryResponse = await this._initUpload(studyId, file, docType);
        const newPresignedUrl = retryResponse.data.presigned_url;

        await axios.put(newPresignedUrl, file, {
          headers: {
            "Content-Type": undefined,
            Authorization: undefined,
          },
          onUploadProgress,
        });
      } else {
        throw error;
      }
    }
  },
  getDocuments: async function ({ studyId }, cancel = false) {
    const response = await api.request({
      url: "/studies/" + studyId + "/documents",
      method: "GET",
      signal: cancel
        ? cancelApiObject[this.getDocuments.name].handleRequestCancellation()
            .signal
        : undefined,
    });
    const documents = response.data.documents
      .map(obj => Object.values(obj))
      .flat();
    return documents;
  },
  deleteDocument: async function ({ studyId, documentId }, cancel = false) {
    const response = await api.request({
      url: "/studies/" + studyId + "/documents/" + documentId,
      method: "DELETE",
      signal: cancel
        ? cancelApiObject[this.deleteDocument.name].handleRequestCancellation()
            .signal
        : undefined,
    });

    return response.data;
  },
  requestCsr: async function ({ studyId }, cancel = false) {
    const response = await api.request({
      url: "/studies/" + studyId + "/csr/request",
      method: "POST",
      signal: cancel
        ? cancelApiObject[this.requestCsr.name].handleRequestCancellation()
            .signal
        : undefined,
    });

    return response.data;
  },
  abortCsr: async function ({ studyId, requestId }, cancel = false) {
    const response = await api.request({
      url: "/studies/" + studyId + "/csr/" + requestId,
      method: "POST",
      signal: cancel
        ? cancelApiObject[this.abortCsr.name].handleRequestCancellation().signal
        : undefined,
    });

    return response.data;
  },
  getCsrStatus: async function ({ studyId, requestId }, cancel = false) {
    const response = await api.request({
      url: "/studies/" + studyId + "/csr/" + requestId + "/status",
      method: "GET",
      signal: cancel
        ? cancelApiObject[this.getCsrStatus.name].handleRequestCancellation()
            .signal
        : undefined,
    });

    return response.data;
  },
  downloadLatestFile: async function ({ studyId, fileURL }, cancel = false) {
    try {
      const presignedUrlResponse = await api.request({
        url: fileURL,
        method: "GET",
        signal: cancel
          ? cancelApiObject[
              this.downloadLatestFile.name
            ].handleRequestCancellation().signal
          : undefined,
      });
      const data = presignedUrlResponse.data;
      let url;
      if (data !== null && typeof data === "object" && !Array.isArray(data)) {
        url = presignedUrlResponse.data.url;
      } else {
        url = presignedUrlResponse.data[0].url;
      }

      if (isProduction) {
        Sentry.setContext("presignedUrl", {
          url: url,
          studyId: studyId,
          timestamp: new Date().toISOString(),
        });
      }

      try {
        const response = await axios.get(url, {
          responseType: "blob",
          signal: cancel
            ? cancelApiObject[
                this.downloadLatestFile.name
              ].handleRequestCancellation().signal
            : undefined,
        });

        if (isProduction) {
          Sentry.setContext("downloadResponse", {
            status: response.status,
            statusText: response.statusText,
            headers: Object.fromEntries(Object.entries(response.headers)),
            size: response.data.size,
            type: response.data.type,
          });
        }

        return response.data;
      } catch (presignedError) {
        const error = new Error("Presigned URL request failed");
        error.originalError = presignedError;
        error.isPresignedUrlError = true;
        error.status = presignedError.response?.status;
        if (isProduction) {
          Sentry.captureException(error);
        }
        throw error;
      }
    } catch (error) {
      if (isProduction) {
        Sentry.captureException(error);
      }
      if (!error.isPresignedUrlError) {
        throw error;
      }
      const newError = new Error("Failed to download CSR");
      newError.originalError = error;
      throw newError;
    }
  },
  deleteStudy: async function (studyId, cancel = false) {
    const response = await api.request({
      url: "/studies/" + studyId,
      method: "DELETE",
      signal: cancel
        ? cancelApiObject[this.deleteStudy.name].handleRequestCancellation()
            .signal
        : undefined,
    });
    return response;
  },
  getFeatureFlags: async function () {
    const response = await api.request({
      url: "/features",
      method: "GET",
    });
    return response;
  },
  login: async function () {
    const response = await api.request({
      url: "/login",
      method: "POST",
    });
    return response;
  },
  logout: async function () {
    const response = await api.request({
      url: "/logout",
      method: "POST",
    });
    return response;
  },
  getToTFLStatus: async function ({ studyId }, cancel = false) {
    const response = await api.request({
      url: `/studies/${studyId}/to-tfl/status`,
      method: "GET",
      signal: cancel
        ? cancelApiObject[this.getToTFLStatus.name].handleRequestCancellation()
            .signal
        : undefined,
    });
    return response.data;
  },
  getAllStatuses: async function ({ studyId }, cancel = false) {
    const response = await api.request({
      url: `/studies/${studyId}/status`,
      method: "GET",
      signal: cancel
        ? cancelApiObject[this.getAllStatuses.name].handleRequestCancellation()
            .signal
        : undefined,
    });
    return response.data;
  },
  getTableNames: async function ({ studyId }, cancel = false) {
    const response = await api.request({
      url: `/studies/${studyId}/tfl-content?content_type=tables`,
      method: "GET",
      signal: cancel
        ? cancelApiObject[this.getTableNames.name].handleRequestCancellation()
            .signal
        : undefined,
    });
    return response.data;
  },
  getFigureNames: async function ({ studyId }, cancel = false) {
    const response = await api.request({
      url: `/studies/${studyId}/tfl-content?content_type=figures`,
      method: "GET",
      signal: cancel
        ? cancelApiObject[this.getFigureNames.name].handleRequestCancellation()
            .signal
        : undefined,
    });
    return response.data;
  },
  getTrafficLightReports: async function ({ studyId }, cancel = false) {
    const response = await api.request({
      url: `/studies/${studyId}/reports`,
      method: "GET",
      signal: cancel
        ? cancelApiObject[
            this.getTrafficLightReports.name
          ].handleRequestCancellation().signal
        : undefined,
    });
    return response.data;
  },
  downloadTrafficLightReport: async function (
    { studyId, reportName },
    cancel = false
  ) {
    try {
      const presignedUrlResponse = await api.request({
        url: `/studies/${studyId}/reports/${encodeURIComponent(reportName)}`,
        method: "GET",
        signal: cancel
          ? cancelApiObject[
              this.downloadTrafficLightReport.name
            ].handleRequestCancellation().signal
          : undefined,
      });

      const { url } = presignedUrlResponse.data;

      if (isProduction) {
        Sentry.setContext("trafficLightReportDownload", {
          url: url,
          studyId: studyId,
          reportName: reportName,
          timestamp: new Date().toISOString(),
        });
      }

      try {
        const response = await axios.get(url, {
          responseType: "blob",
          signal: cancel
            ? cancelApiObject[
                this.downloadTrafficLightReport.name
              ].handleRequestCancellation().signal
            : undefined,
        });

        if (isProduction) {
          Sentry.setContext("trafficLightReportDownloadResponse", {
            status: response.status,
            statusText: response.statusText,
            headers: Object.fromEntries(Object.entries(response.headers)),
            size: response.data.size,
            type: response.data.type,
          });
        }

        return response.data;
      } catch (presignedError) {
        const error = new Error(
          "Traffic light report presigned URL request failed"
        );
        error.originalError = presignedError;
        error.isPresignedUrlError = true;
        error.status = presignedError.response?.status;
        if (isProduction) {
          Sentry.captureException(error);
        }
        throw error;
      }
    } catch (error) {
      if (isProduction) {
        Sentry.captureException(error);
      }
      if (!error.isPresignedUrlError) {
        throw error;
      }
      const newError = new Error("Failed to download traffic light report");
      newError.originalError = error;
      throw newError;
    }
  },
  deleteAllTfls: async function ({ studyId }, cancel = false) {
    const response = await api.request({
      url: `/studies/${studyId}/tfls`,
      method: "DELETE",
      signal: cancel
        ? cancelApiObject[this.deleteAllTfls.name].handleRequestCancellation()
            .signal
        : undefined,
    });
    return response.data;
  },
  reloadTfls: async function ({ studyId }, cancel = false) {
    const response = await api.request({
      url: `/studies/${studyId}/tfls/reload`,
      method: "POST",
      signal: cancel
        ? cancelApiObject[this.reloadTfls.name].handleRequestCancellation()
            .signal
        : undefined,
    });
    return response.data;
  },
  filterTables: async function ({ studyId }, cancel = false) {
    const response = await api.request({
      url: `/studies/${studyId}/filter`,
      method: "POST",
      data: {},
      signal: cancel
        ? cancelApiObject[this.filterTables.name].handleRequestCancellation()
            .signal
        : undefined,
    });

    return response.data;
  },
  getAdvSearchDashboard: async function ({ studyId }, cancel = false) {
    console.log(studyId);
    const response = await api.request({
      url: "/advanced_search/" + studyId + "/dashboard",
      method: "GET",
      signal: cancel
        ? cancelApiObject[
            this.getAdvSearchDashboard.name
          ].handleRequestCancellation().signal
        : undefined,
    });
    return response.data;
  },
  getTableConfiguration: async function ({ studyId, tableId }, cancel = false) {
    const response = await api.request({
      url: "/advanced_search/" + studyId + "/tables/" + tableId + "/queries",
      method: "GET",
      signal: cancel
        ? cancelApiObject[
            this.getTableConfiguration.name
          ]?.handleRequestCancellation?.()?.signal
        : undefined,
    });
    return response.data;
  },
  putTableGeneration: async function (
    { studyId, tableId, body },
    cancel = false
  ) {
    const response = await api.request({
      url: "/advanced_search/" + studyId + "/tables/" + tableId + "/generation",
      method: "POST",
      data: body,
      signal: cancel
        ? cancelApiObject[
            this.putTableGeneration.name
          ]?.handleRequestCancellation?.()?.signal
        : undefined,
    });
    return response.data;
  },
  getTableRequestStatus: async function ({ studyId, tableId }, cancel = false) {
    const response = await api.request({
      url:
        "/advanced_search/" +
        studyId +
        "/tables/" +
        tableId +
        "/request_status",
      method: "GET",
      signal: cancel
        ? cancelApiObject[
            this.getTableRequestStatus.name
          ]?.handleRequestCancellation?.()?.signal
        : undefined,
    });
    return response.data;
  },
  getTableOutput: async function ({ studyId, tableId }, cancel = false) {
    const response = await api.request({
      url: "/advanced_search/" + studyId + "/tables/" + tableId + "/output",
      method: "GET",
      signal: cancel
        ? cancelApiObject[
            this.getTableOutput.name
          ]?.handleRequestCancellation?.()?.signal
        : undefined,
    });
    return response.data;
  },
  putEditedResponse: async function (
    { studyId, tableId, editedContent },
    cancel = false
  ) {
    const response = await api.request({
      url: "/advanced_search/" + studyId + "/tables/" + tableId + "/output",
      method: "POST",
      data: { edited_output: editedContent },
      signal: cancel
        ? cancelApiObject[
            this.putEditedResponse.name
          ]?.handleRequestCancellation?.()?.signal
        : undefined,
    });
    return response.data;
  },
  //Lay Summary specific APIs
  getAllStudiesForLaySummaries: async function (cancel = false) {
    const response = await api.request({
      url: "/studies?status=RENDER_AVAILABLE",
      method: "GET",
      signal: cancel
        ? cancelApiObject[this.getAllStudies.name].handleRequestCancellation()
            .signal
        : undefined,
    });
    const studies = response.data.studies.map(obj => Object.values(obj)).flat();
    return studies;
  },
  getAllLaySummaries: async function (cancel = false) {
    const response = await api.request({
      url: "/lay-summaries",
      method: "GET",
      signal: cancel
        ? cancelApiObject[
            this.getAllLaySummaries.name
          ].handleRequestCancellation().signal
        : undefined,
    });
    const studies = response.data.studies.map(obj => Object.values(obj)).flat();
    return studies;
  },
  createLaySummary: async function (
    { laySummaryName, selectedStudyIDValue, selectedStudy },
    cancel = false
  ) {
    const response = await api.request({
      url: "/lay-summaries",
      method: "POST",
      data: {
        lay_summary_name: laySummaryName,
        CSR_study_id: Number(selectedStudyIDValue),
        CSR_study_type: "INTERNAL_CSR",
        CSR_study_name: selectedStudy,
      },
      signal: cancel
        ? cancelApiObject[
            this.createLaySummary.name
          ].handleRequestCancellation().signal
        : undefined,
    });
    return response.data;
  },
  deleteLaySummary: async function (lay_summary_id, cancel = false) {
    const response = await api.request({
      url: "/lay-summaries/" + lay_summary_id,
      method: "DELETE",
      signal: cancel
        ? cancelApiObject[
            this.deleteLaySummary.name
          ].handleRequestCancellation().signal
        : undefined,
    });
    return response;
  },
  getLaySummaryQuestions: async function ({ studyId }, cancel = false) {
    const response = await api.request({
      url: "/lay-summaries/" + studyId + "/wizards",
      method: "GET",
      signal: cancel
        ? cancelApiObject[
            this.getLaySummaryQuestions.name
          ].handleRequestCancellation().signal
        : undefined,
    });

    return response.data;
  },
  saveLaySummaryQuestions: async function ({ studyId, state }, cancel = false) {
    const response = await api.request({
      url: "/lay-summaries/" + studyId + "/wizards",
      method: "PUT",
      data: state,
      signal: cancel
        ? cancelApiObject[
            this.saveLaySummaryQuestions.name
          ].handleRequestCancellation().signal
        : undefined,
    });

    return response.data;
  },
  getLatestLaySummaryRequest: async function (
    { lay_summary_id },
    cancel = false
  ) {
    const response = await api.request({
      url: "/lay-summaries/" + lay_summary_id + "/requests-latest",
      method: "GET",
      signal: cancel
        ? cancelApiObject[
            this.getLatestLaySummaryRequest.name
          ].handleRequestCancellation().signal
        : undefined,
    });

    return response.data;
  },
  generateLaySummary: async function ({ studyId }, cancel = false) {
    const response = await api.request({
      url: "/lay-summaries/" + studyId,
      method: "POST",
      signal: cancel
        ? cancelApiObject[
            this.generateLaySummary.name
          ].handleRequestCancellation().signal
        : undefined,
    });

    return response.data;
  },
  getLaySummaryStatus: async function (
    { lay_summary_id, requestId },
    cancel = false
  ) {
    const response = await api.request({
      url:
        "/lay-summaries/" +
        lay_summary_id +
        "/requests/" +
        requestId +
        "/status",
      method: "GET",
      signal: cancel
        ? cancelApiObject[
            this.getLaySummaryStatus.name
          ].handleRequestCancellation().signal
        : undefined,
    });

    return response.data;
  },
  abortLaySummaryGeneration: async function (
    { studyId, requestId },
    cancel = false
  ) {
    const response = await api.request({
      url: "/lay-summaries/" + studyId + "/lay-summaries/" + requestId,
      method: "POST",
      signal: cancel
        ? cancelApiObject[
            this.abortLaySummaryGeneration.name
          ].handleRequestCancellation().signal
        : undefined,
    });

    return response.data;
  },

  // ── TriloChat ──────────────────────────────────────────────────────────────

  listChatSessions: async function () {
    const response = await api.request({
      url: "/chat/sessions",
      method: "GET",
    });
    return response.data;
  },

  createChatSession: async function ({ studyId }) {
    const response = await api.request({
      url: "/chat/sessions",
      method: "POST",
      data: { study_id: studyId || null },
    });
    return response.data;
  },

  getChatSession: async function ({ sessionId }) {
    const response = await api.request({
      url: "/chat/sessions/" + sessionId,
      method: "GET",
    });
    return response.data;
  },

  deleteChatSession: async function ({ sessionId }) {
    const response = await api.request({
      url: "/chat/sessions/" + sessionId,
      method: "DELETE",
    });
    return response.data;
  },
};

// defining the cancel API object for CsrAPI
const cancelApiObject = defineCancelApiObject(CsrAPI);
